

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isSuperAdminRole } from '../utils/roles';

export default function ProtectedRoute({ children, superAdminOnly = false }) {
  const { isAuthenticated, loading, user } = useAuth();
  const navigate = useNavigate();

  const hasBeenAuthenticated = useRef(false);

  if (isAuthenticated) {
    hasBeenAuthenticated.current = true;
  }

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  // Definitively signed out — never render the protected page, no matter that
  // this user was authenticated a moment ago. `hasBeenAuthenticated` is
  // deliberately NOT consulted here: it latches true forever on first sign-in,
  // so including it meant that after logout this fell through to `children` and
  // the dashboard stayed mounted, fetching with a cleared token and rendering
  // the API's 401 as "Invalid or expired session" until the redirect landed.
  if (!loading && !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Redirecting...</p>
        </div>
      </div>
    );
  }

  // Still checking. The latch belongs here and only here: a background
  // re-validation for someone already signed in must not blank the page they
  // are looking at.
  if (loading && !hasBeenAuthenticated.current) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // A UX gate only — the API's own requireSuperAdmin middleware is what actually
  // protects the admin endpoints, so this just avoids rendering a page whose
  // every request would 403 anyway.
  if (superAdminOnly && !isSuperAdminRole(user?.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div data-testid="lock-card" className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-10 text-center shadow-sm">
          <div className="mb-3 text-3xl" aria-hidden="true">&#128274;</div>
          <h1 className="text-lg font-bold text-gray-900">Only the Super Admin can change this.</h1>
          <p className="mt-2 text-sm text-gray-500">Points rules, manual point changes, badges and the bad words list are managed by the Super Admin.</p>
          <Link to="/community" className="mt-6 inline-block rounded-lg border border-gray-300 px-5 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50">Back to community</Link>
        </div>
      </div>
    );
  }

  return children;
}
